import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { employeeApi, payrollApi, companyApi, projectApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { canEditPayroll } from '@/lib/roles';
import { formatCurrency, recalculatePayrollEmployee, resolvePaymentSplit, resolvePayrollDaysInMonth, getCalendarDaysInMonth } from '@/lib/payrollCalc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Play, Lock, CheckCircle, Download, Plus, Receipt, History, Printer, Search, ArrowUpDown, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PaySlipModal } from '@/components/PaySlipModal';
import { ExportFormatDialog } from '@/components/ExportFormatDialog';
import { downloadBlobFile, exportMultiSectionPdf } from '@/lib/exportFile';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Payroll PDF columns matching the salary sheet layout. */
const PAYRUN_EXPORT_HEADER = [
  'SL. NO.',
  'NAME OF EMPLOYEE',
  'Rate',
  'Weekly off / Rest',
  'Days',
  'Old Advance',
  'Current Advance',
  'Advance Deduction',
  'Leave Deduction',
  'Extra Work Payment',
  'Salary Payable',
  'Mobile & Petrol',
  'PF & ESIC DEDUCTION',
  'Net Amt. Payable',
  'Bank Transfer',
  'Other',
  'Balance Advance',
];

const PAYRUN_PDF_UNSELECTED_COLUMNS = [];

const toPayrollAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatWeeklyOffRest = (emp) => {
  const days = Array.isArray(emp?.weeklyOffDays) ? emp.weeklyOffDays.filter(Boolean) : [];
  if (days.length === 1) return days[0];
  if (days.length > 1) return `${days.length} Rest`;
  return '—';
};

const buildPayrollSortParams = (sortOrder) => {
  if (sortOrder !== 'asc' && sortOrder !== 'desc') return {};
  return { sortBy: 'salary', sortOrder };
};

const unwrapPayRunDetail = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.payRunId || payload.totalGross != null || payload.employees) return payload;
  if (payload.data && typeof payload.data === 'object') return unwrapPayRunDetail(payload.data);
  return payload;
};

const SUM_PDF_COLUMNS = new Set([
  'Rate',
  'Old Advance',
  'Current Advance',
  'Advance Deduction',
  'Leave Deduction',
  'Extra Work Payment',
  'Salary Payable',
  'Mobile & Petrol',
  'PF & ESIC DEDUCTION',
  'Net Amt. Payable',
  'Bank Transfer',
  'Other',
  'Balance Advance',
]);

const PAYRUN_PDF_COLUMN_WEIGHTS = {
  'SL. NO.': 5,
  'NAME OF EMPLOYEE': 16,
  Rate: 11,
  'Weekly off / Rest': 10,
  Days: 6,
  'Old Advance': 7,
  'Current Advance': 7,
  'Advance Deduction': 7,
  'Leave Deduction': 7,
  'Extra Work Payment': 12,
  'Salary Payable': 12,
  'Mobile & Petrol': 11,
  'PF & ESIC DEDUCTION': 12,
  'Net Amt. Payable': 12,
  'Bank Transfer': 11,
  Other: 8,
  'Balance Advance': 12,
};

const PAYRUN_PDF_NARROW_COLUMNS = [
  'Old Advance',
  'Current Advance',
  'Advance Deduction',
  'Leave Deduction',
];

const PAYRUN_PDF_HEADER_LABELS = {
  'SL. NO.': 'SL.\nNO.',
  'NAME OF EMPLOYEE': 'NAME OF\nEMPLOYEE',
  'Weekly off / Rest': 'Weekly off\n/ Rest',
  'Old Advance': 'Old\nAdvance',
  'Current Advance': 'Current\nAdvance',
  'Advance Deduction': 'Advance\nDeduction',
  'Leave Deduction': 'Leave\nDeduction',
  'Extra Work Payment': 'Extra Work\nPayment',
  'Salary Payable': 'Salary\nPayable',
  'Mobile & Petrol': 'Mobile &\nPetrol',
  'PF & ESIC DEDUCTION': 'PF & ESIC\nDEDUCTION',
  'Net Amt. Payable': 'Net Amt.\nPayable',
  'Bank Transfer': 'Bank\nTransfer',
  'Balance Advance': 'Balance\nAdvance',
};

const PAYRUN_PDF_NUMERIC_COLUMNS = [
  'Rate',
  'Days',
  'Old Advance',
  'Current Advance',
  'Advance Deduction',
  'Leave Deduction',
  'Extra Work Payment',
  'Salary Payable',
  'Mobile & Petrol',
  'PF & ESIC DEDUCTION',
  'Net Amt. Payable',
  'Bank Transfer',
  'Other',
  'Balance Advance',
];

const buildPayRunPdfTotalRow = (rows = [], header = []) => {
  if (!header.length) return null;
  const totalsByColumn = header.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});
  const labelKey = header.includes('NAME OF EMPLOYEE') ? 'NAME OF EMPLOYEE' : header[0];
  if (labelKey) totalsByColumn[labelKey] = 'TOTAL';

  SUM_PDF_COLUMNS.forEach((key) => {
    if (!header.includes(key)) return;
    totalsByColumn[key] = rows.reduce((sum, row) => sum + toPayrollAmount(row?.[key]), 0);
  });

  return totalsByColumn;
};

const MONTH_DAYS_SUMMARY_HEADER = ['Particulars', 'Days'];

const countSundaysInMonth = (month, year) => {
  const days = getCalendarDaysInMonth(month, year);
  let sundays = 0;
  for (let day = 1; day <= days; day += 1) {
    if (new Date(year, month - 1, day).getDay() === 0) sundays += 1;
  }
  return sundays;
};

const buildMonthDaysSummaryRows = (month, year) => {
  const monthTotalDays = getCalendarDaysInMonth(month, year);
  const leaveSunday = countSundaysInMonth(month, year);
  const leave = 2;
  return [
    { Particulars: 'Month Total Days', Days: monthTotalDays },
    { Particulars: 'Leave Sunday', Days: leaveSunday },
    { Particulars: 'Balance', Days: monthTotalDays - leaveSunday },
    { Particulars: 'Month Total Days', Days: monthTotalDays },
    { Particulars: 'Leave', Days: leave },
    { Particulars: 'Balance', Days: monthTotalDays - leave },
  ];
};

const JOINED_EXITED_EXPORT_HEADER = [
  'Employee ID',
  'Name',
  'Department',
  'Designation',
  'Joining Date',
  'Exit Date',
  'Status',
];

const normalizeEmployeeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.employees)) return value.employees;
  return [];
};

const normalizeJoinedExitedResponse = (response) => {
  if (!response) return { joined: [], exited: [] };

  const payload =
    response?.joined || response?.exited
      ? response
      : response?.data?.joined || response?.data?.exited
        ? response.data
        : response?.items?.joined || response?.items?.exited
          ? response.items
          : response?.data ?? response;

  return {
    joined: normalizeEmployeeList(payload?.joined ?? payload?.joinedEmployees),
    exited: normalizeEmployeeList(payload?.exited ?? payload?.exitedEmployees),
  };
};

const parseDateParts = (value) => {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { year: Number(iso[1]), month: Number(iso[2]) };
  const dmy = raw.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})/);
  if (dmy) return { year: Number(dmy[3]), month: Number(dmy[2]) };
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return { year: parsed.getFullYear(), month: parsed.getMonth() + 1 };
};

const isDateInMonth = (value, month, year) => {
  const parts = parseDateParts(value);
  if (!parts) return false;
  return parts.month === Number(month) && parts.year === Number(year);
};

const getEmployeeJoinDate = (emp) =>
  emp?.joiningDate || emp?.joinDate || emp?.dateOfJoining || emp?.doj || '';

const getEmployeeExitDate = (emp) =>
  emp?.inactiveDate || emp?.exitDate || emp?.relievingDate || emp?.lastWorkingDate || '';

const filterJoinedForMonth = (employees = [], month, year) =>
  employees.filter((emp) => isDateInMonth(getEmployeeJoinDate(emp), month, year));

const filterExitedForMonth = (employees = [], month, year) =>
  employees.filter((emp) => isDateInMonth(getEmployeeExitDate(emp), month, year));

const mapJoinedExitedEmployeeRow = (emp) => ({
  'Employee ID': emp?.empId || emp?.employeeId || emp?.id || '',
  Name:
    emp?.name ||
    emp?.employeeName ||
    `${emp?.firstName || ''} ${emp?.lastName || ''}`.trim(),
  Department: emp?.department || '',
  Designation: emp?.designation || '',
  'Joining Date': emp?.joiningDate || '',
  'Exit Date':
    emp?.inactiveDate ||
    emp?.exitDate ||
    emp?.relievingDate ||
    emp?.lastWorkingDate ||
    '',
  Status: emp?.status || '',
});

const buildJoinedExitedExportRows = (employees = []) =>
  employees.map((emp) => mapJoinedExitedEmployeeRow(emp));

const applyCalendarDayPayroll = (payRun, month, year) => {
  if (!payRun?.employees?.length) return payRun;
  const options = {
    month: payRun.month ?? month,
    year: payRun.year ?? year,
  };
  const employees = payRun.employees.map((emp) => recalculatePayrollEmployee(emp, {}, options));
  return {
    ...payRun,
    employees,
    daysInMonth: resolvePayrollDaysInMonth(employees[0], options),
    totalGross: employees.reduce((sum, emp) => sum + (emp?.effectiveGross ?? 0), 0),
    totalNet: employees.reduce((sum, emp) => sum + (emp?.netPay ?? 0), 0),
    totalDeductions: employees.reduce((sum, emp) => sum + (emp?.totalDeductions ?? 0), 0),
  };
};

const applyPayRunDetail = (detail, month, year) => {
  const calculated = applyCalendarDayPayroll(detail, month, year);
  if (!detail) return calculated;
  return {
    ...calculated,
    totalGross: detail.totalGross ?? calculated?.totalGross,
    totalNet: detail.totalNet ?? calculated?.totalNet,
    totalDeductions: detail.totalDeductions ?? calculated?.totalDeductions,
    totalAdvanceDeductions: detail.totalAdvanceDeductions ?? calculated?.totalAdvanceDeductions,
    totalPF: detail.totalPF ?? calculated?.totalPF,
    totalESIC: detail.totalESIC ?? calculated?.totalESIC,
    totalStatutoryDeductions: detail.totalStatutoryDeductions ?? calculated?.totalStatutoryDeductions,
    workingDays: detail.workingDays ?? calculated?.workingDays,
  };
};

const draftEditableState = (employees = []) => {
  const ed = {};
  employees.forEach((emp) => {
    if (!emp?.employeeId) return;
    const inHand = Math.max(Number(emp?.inHandSalary ?? emp?.netPay) || 0, 0);
    const { byBank, other } = resolvePaymentSplit(inHand, {}, emp);
    ed[emp.employeeId] = {
      bonusDays: emp.bonusDays ?? 0,
      manualLopAdd: emp.manualLopAdd ?? 0,
      leaveAdj: emp.manualLopAdd ?? 0,
      advanceOverride: emp.advanceDeduction ?? 0,
      byBank,
      other,
    };
  });
  return ed;
};

const resolveAmountFromCandidates = (candidates = []) => {
  for (const value of candidates) {
    if (value == null || value === '') continue;
    const amount = Number(value);
    if (Number.isFinite(amount)) return amount;
  }
  return 0;
};

const resolvePfDeduction = (payrollEmp, source = {}) =>
  resolveAmountFromCandidates([
    payrollEmp?.pf,
    payrollEmp?.epf,
    payrollEmp?.pfDeduction,
    source?.pf,
    source?.epf,
    source?.salary?.epf,
    source?.salary?.pf,
  ]);

const resolveEsicDeduction = (payrollEmp, source = {}) =>
  resolveAmountFromCandidates([
    payrollEmp?.esi,
    payrollEmp?.esic,
    payrollEmp?.esicDeduction,
    source?.esi,
    source?.esic,
    source?.salary?.esi,
    source?.salary?.esic,
  ]);

const resolvePfEsicDeduction = (payrollEmp, source = {}) =>
  resolvePfDeduction(payrollEmp, source) + resolveEsicDeduction(payrollEmp, source);

const InHandAmount = ({ employee }) => {
  const inHand = Number(employee?.inHandSalary ?? employee?.netPay) || 0;
  const arrearsAmount = Number(employee?.arrearsAmount) || 0;

  return (
    <span className="inline-flex items-center justify-end gap-1">
      {formatCurrency(inHand)}
      {arrearsAmount > 0 && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex text-[#10b981]/80 hover:text-[#10b981] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#10b981] rounded-full"
                aria-label={`In Hand ${formatCurrency(inHand)} plus arrears ${formatCurrency(arrearsAmount)}`}
                data-testid={`arrears-info-${employee?.employeeId}`}
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="font-mono text-xs">
              {formatCurrency(inHand)} + {formatCurrency(arrearsAmount)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
};

export default function PayrollPage() {
  const { user } = useAuth();
  const canEdit = canEditPayroll(user?.role);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allPayRuns, setAllPayRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [salarySortOrder, setSalarySortOrder] = useState('');
  const [payRunData, setPayRunData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [paySlipOpen, setPaySlipOpen] = useState(false);
  const [paySlipEmp, setPaySlipEmp] = useState(null);
  const [paySlipData, setPaySlipData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportTarget, setExportTarget] = useState(null);

  const [processPage, setProcessPage] = useState(1);
  const [processPageSize, setProcessPageSize] = useState(10);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(20);

  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };

  const normalizeListResponse = (value) => {
    if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pages: 1 };
    if (Array.isArray(value?.items)) return { items: value.items, total: value?.total ?? value.items.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
    return { items: [], total: 0, page: 1, pages: 1 };
  };

  // Load the selected month's pay run from GET /api/payroll/:id with salary sort.
  useEffect(() => {
    const pr = allPayRuns.find((p) => p.month === selectedMonth && p.year === selectedYear);
    if (!pr?.payRunId) {
      setPayRunData(null);
      setEditableData({});
      return undefined;
    }

    let cancelled = false;
    const loadPayRun = async () => {
      try {
        const detail =
          unwrapPayRunDetail(
            await payrollApi.getById(pr.payRunId, buildPayrollSortParams(salarySortOrder))
          ) || pr;
        if (cancelled) return;
        const payRun = applyPayRunDetail(detail, selectedMonth, selectedYear);
        setPayRunData(payRun);
        setEditableData(
          payRun?.status === 'Draft' && Array.isArray(payRun?.employees)
            ? draftEditableState(payRun.employees)
            : {}
        );
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch pay run:', err);
        const payRun = applyPayRunDetail(pr, selectedMonth, selectedYear);
        setPayRunData(payRun);
        setEditableData(
          pr.status === 'Draft' && Array.isArray(pr.employees) ? draftEditableState(pr.employees) : {}
        );
      }
    };

    loadPayRun();
    return () => {
      cancelled = true;
    };
  }, [allPayRuns, selectedMonth, selectedYear, salarySortOrder]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const payrollParams = {
          page: historyPage,
          limit: historyPageSize,
        };
        if (selectedCompany !== 'all') payrollParams.company = selectedCompany;
        if (selectedProject !== 'all') payrollParams.project = selectedProject;
        const trimmedSearch = debouncedSearch?.trim?.();
        if (trimmedSearch) payrollParams.search = trimmedSearch;

        const [empsData, companiesData, projectsData, payRunsData] = await Promise.all([
          employeeApi.getAll().catch(() => []),
          companyApi.getAll().catch(() => []),
          projectApi.getAll().catch(() => []),
          payrollApi.getAll(payrollParams).catch(() => []),
        ]);
        setEmployees(normalizeArrayResponse(empsData));
        setCompanies(normalizeArrayResponse(companiesData));
        setProjects(normalizeArrayResponse(projectsData));
        const normalizedPayRuns = normalizeListResponse(payRunsData);
        setAllPayRuns(normalizedPayRuns.items);
        setHistoryTotal(normalizedPayRuns.total);
        setHistoryTotalPages(normalizedPayRuns.pages);
        setHistoryPage((p) => clampPageToTotal(p, normalizedPayRuns.pages));
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedMonth, selectedYear, selectedCompany, selectedProject, debouncedSearch, historyPage, historyPageSize]);

  useEffect(() => {
    setHistoryPage(1);
  }, [selectedCompany, selectedProject, debouncedSearch]);

  const activeEmps = employees.filter(e => e.status === 'Active');

  useEffect(() => { setProcessPage(1); }, [payRunData?.payRunId, debouncedSearch, salarySortOrder]);

  const processEmployees = payRunData?.employees ?? [];
  const processTotalPages = Math.max(Math.ceil(processEmployees.length / processPageSize), 1);
  const paginatedProcessEmps = processEmployees.slice(
    (processPage - 1) * processPageSize,
    processPage * processPageSize
  );
  const showAnnualBonusColumn = processEmployees.some(
    (emp) => toPayrollAmount(emp?.netAnnualBonus) !== 0
  );
  const processTableColSpan = showAnnualBonusColumn ? 18 : 17;

  const paginatedHistory = allPayRuns;

  const payrollSortParams = () => buildPayrollSortParams(salarySortOrder);

  const hydratePayRunFromApi = async (id, fallback) => {
    try {
      const detail = unwrapPayRunDetail(await payrollApi.getById(id, payrollSortParams()));
      if (detail) return applyPayRunDetail(detail, selectedMonth, selectedYear);
    } catch (err) {
      console.error('Failed to reload sorted pay run:', err);
    }
    return fallback ? applyPayRunDetail(fallback, selectedMonth, selectedYear) : null;
  };

  const generatePayRun = async () => {
    setGenerating(true);
    try {
      // Collect bonus days and leave adjustments from editable data
      const bonusDays = {};
      const leaveAdjustments = {};
      const advanceOverrides = {};
      const byBank = {};
      const other = {};
      Object.entries(editableData).forEach(([empId, data]) => {
        if (data?.bonusDays !== undefined) bonusDays[empId] = data.bonusDays;
        if (data?.manualLopAdd !== undefined) leaveAdjustments[empId] = data.manualLopAdd;
        else if (data?.leaveAdj !== undefined) leaveAdjustments[empId] = data.leaveAdj;
        if (data?.advanceOverride !== undefined) advanceOverrides[empId] = Math.max(parseFloat(data.advanceOverride) || 0, 0);
        if (data?.byBank !== undefined) byBank[empId] = Math.max(parseFloat(data.byBank) || 0, 0);
        if (data?.other !== undefined) other[empId] = Math.max(parseFloat(data.other) || 0, 0);
      });

      const generated = await payrollApi.generate(
        selectedMonth,
        selectedYear,
        bonusDays,
        leaveAdjustments,
        advanceOverrides,
        byBank,
        other
      );
      const payRun = await hydratePayRunFromApi(generated?.payRunId, generated);
      if (!payRun?.payRunId) throw new Error('Failed to load generated pay run');
      setPayRunData(payRun);
      
      // Update allPayRuns
      setAllPayRuns(prev => {
        const idx = prev.findIndex(pr => pr.payRunId === payRun.payRunId);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = payRun;
          return updated;
        }
        return [...prev, payRun];
      });

      toast.success('Pay run generated');
    } catch (err) {
      toast.error(err.message || 'Failed to generate pay run');
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePayRunClick = () => {
    setSelectedCompany('all');
    setSelectedProject('all');
    setConfirmAction('generate');
  };

  const getPayRunDayOptions = (run = payRunData) => ({
    month: run?.month ?? selectedMonth,
    year: run?.year ?? selectedYear,
  });

  const clampManualLop = (employee, rawManualLop) => {
    const daysInMonth = resolvePayrollDaysInMonth(employee, getPayRunDayOptions());
    const parsed = Number(rawManualLop);
    const rawManualLopAdd = Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;

    if (daysInMonth <= 0) {
      return {
        daysInMonth,
        rawManualLopAdd,
        manualLopAdd: rawManualLopAdd,
        exceeded: false,
      };
    }

    const manualLopAdd = Math.min(rawManualLopAdd, daysInMonth);
    return {
      daysInMonth,
      rawManualLopAdd,
      manualLopAdd,
      exceeded: rawManualLopAdd > daysInMonth,
    };
  };

  const updateEmployeeLiveValues = (empId, overrides = {}) => {
    if (!payRunData?.employees) return;
    const options = getPayRunDayOptions();

    const updatedEmployees = payRunData.employees.map((emp) =>
      emp.employeeId === empId ? recalculatePayrollEmployee(emp, overrides, options) : emp
    );

    const updatedEmp = updatedEmployees.find((emp) => emp.employeeId === empId);
    if (updatedEmp) {
      setEditableData((prev) => ({
        ...prev,
        [empId]: {
          ...prev[empId],
          byBank: updatedEmp.byBank,
          other: updatedEmp.other,
        },
      }));
    }

    const totalGross = updatedEmployees.reduce((sum, emp) => sum + (emp?.effectiveGross ?? 0), 0);
    const totalNet = updatedEmployees.reduce((sum, emp) => sum + (emp?.netPay ?? 0), 0);
    const totalDeductions = updatedEmployees.reduce((sum, emp) => sum + (emp?.totalDeductions ?? 0), 0);

    setPayRunData((prev) => ({
      ...prev,
      employees: updatedEmployees,
      totalGross,
      totalNet,
      totalDeductions,
    }));
  };

  const handleBonusDaysChange = (empId, value) => {
    const nextBonusDays = parseFloat(value) || 0;
    setEditableData((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], bonusDays: nextBonusDays },
    }));
    updateEmployeeLiveValues(empId, { bonusDays: nextBonusDays });
  };

  const handleManualLopAddChange = (empId, value) => {
    const parsed = value === '' || value === null || value === undefined ? '' : value;
    const numeric = parsed === '' ? 0 : parseFloat(parsed);
    const manualLopAdd = Number.isFinite(numeric) ? Math.max(numeric, 0) : 0;

    setEditableData((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        leaveAdj: manualLopAdd,
        manualLopAdd: parsed === '' ? '' : manualLopAdd,
      },
    }));

    if (parsed !== '') {
      updateEmployeeLiveValues(empId, { manualLopAdd });
    }
  };

  const handleManualLopAddBlur = (empId, inputValue) => {
    const emp = payRunData?.employees?.find((e) => e.employeeId === empId);
    if (!emp) return;

    const rawFromInput = inputValue !== undefined && inputValue !== null
      ? inputValue
      : editableData[empId]?.manualLopAdd;
    const { daysInMonth, manualLopAdd, exceeded } = clampManualLop(emp, rawFromInput);

    if (exceeded) {
      toast.error(`Manual LOP cannot exceed calendar days (${daysInMonth})`);
    }

    setEditableData((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], leaveAdj: manualLopAdd, manualLopAdd },
    }));
    updateEmployeeLiveValues(empId, { manualLopAdd });
  };

  const handleAdvanceOverrideChange = (empId, value) => {
    const advanceOverride = Math.max(parseFloat(value) || 0, 0);
    setEditableData((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], advanceOverride },
    }));
    updateEmployeeLiveValues(empId, { advanceOverride });
  };

  const handleByBankChange = (empId, value) => {
    const byBank = Math.max(parseFloat(value) || 0, 0);
    setEditableData((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], byBank },
    }));
    updateEmployeeLiveValues(empId, { byBank });
  };

  const handleOtherChange = (empId, value) => {
    const other = Math.max(parseFloat(value) || 0, 0);
    setEditableData((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], other },
    }));
    updateEmployeeLiveValues(empId, { other });
  };

  const freezePayRun = async () => {
    if (!payRunData) return;
    try {
      const frozen = await payrollApi.freeze(payRunData.payRunId);
      const updated = await hydratePayRunFromApi(payRunData.payRunId, frozen);
      if (!updated?.payRunId) throw new Error('Failed to reload pay run');
      setPayRunData(updated);
      setAllPayRuns(prev => prev.map(pr => pr.payRunId === updated.payRunId ? updated : pr));
      toast.success('Pay run frozen - Yet to Pay');
    } catch (err) {
      toast.error(err.message || 'Failed to freeze pay run');
    }
  };

  const handleConfirmAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === 'saveDraft' || action === 'generate') await generatePayRun();
    if (action === 'freeze') await freezePayRun();
    if (action === 'process') await processPayRun();
  };

  const confirmDialogCopy = {
    generate: {
      title: 'Generate Pay Run?',
      description: `Generate a pay run for ${MONTHS[selectedMonth - 1]} ${selectedYear}? This will recalculate payroll for all active employees.`,
      actionLabel: 'Generate Pay Run',
      actionClassName: 'bg-[#0047AB] hover:bg-[#003d96] text-white',
    },
    saveDraft: {
      title: 'Save Draft?',
      description: `Save the current draft for ${MONTHS[selectedMonth - 1]} ${selectedYear}? Bonus days, LOP adjustments, advance overrides, and payment split will be recalculated and saved.`,
      actionLabel: 'Save Draft',
      actionClassName: '',
    },
    freeze: {
      title: 'Freeze Pay Run?',
      description: `Freeze the pay run for ${MONTHS[selectedMonth - 1]} ${selectedYear}? Once frozen, edits will be locked and the status will change to Yet to Pay.`,
      actionLabel: 'Freeze Pay Run',
      actionClassName: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    },
    process: {
      title: 'Process Payroll?',
      description: `Process payroll for ${MONTHS[selectedMonth - 1]} ${selectedYear}? This marks the pay run as paid and cannot be undone easily.`,
      actionLabel: 'Process Payroll',
      actionClassName: 'bg-green-600 hover:bg-green-700 text-white',
    },
  };

  const processPayRun = async () => {
    if (!payRunData) return;
    try {
      const processed = await payrollApi.process(payRunData.payRunId);
      const updated = await hydratePayRunFromApi(payRunData.payRunId, processed);
      if (!updated?.payRunId) throw new Error('Failed to reload pay run');
      setPayRunData(updated);
      setAllPayRuns(prev => prev.map(pr => pr.payRunId === updated.payRunId ? updated : pr));
      toast.success('Payroll processed successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to process payroll');
    }
  };

  const buildPayRunExportRows = (pr) => {
    if (!pr?.employees?.length) return [];
    const calculated = applyCalendarDayPayroll(pr, pr.month ?? selectedMonth, pr.year ?? selectedYear);
    return calculated.employees.map((e, index) => {
      const fullEmp =
        employees.find((emp) => emp?.empId === e?.employeeId || emp?.employeeId === e?.employeeId) || {};
      const source = { ...fullEmp, ...e };
      const gross = toPayrollAmount(e?.gross);
      const perDaySalary = toPayrollAmount(e?.perDayRate) || (e?.daysInMonth > 0 ? gross / e.daysInMonth : 0);
      const lopDays = toPayrollAmount(e?.lopDays);
      const bonusDays = toPayrollAmount(e?.bonusDays);
      // Use LOP days (attendance + manual), not absent days — paid leave is absent but not deducted
      const leaveDeduction =
        e?.lopDeduction != null
          ? toPayrollAmount(e.lopDeduction)
          : Math.round(lopDays * perDaySalary);
      const extraWorkPayment = Math.round(bonusDays * perDaySalary);
      const inHand = toPayrollAmount(e?.inHandSalary ?? e?.netPay);
      const pfEsic = resolvePfEsicDeduction(e, source);
      const { byBank, other } = resolvePaymentSplit(inHand, {}, e);
      return {
        'SL. NO.': index + 1,
        'NAME OF EMPLOYEE': e?.employeeName || '',
        Rate: gross,
        'Weekly off / Rest': formatWeeklyOffRest(e),
        Days: toPayrollAmount(e?.presentDays),
        'Old Advance': toPayrollAmount(e?.oldAdvance),
        'Current Advance': toPayrollAmount(e?.currentAdvance),
        'Advance Deduction': toPayrollAmount(e?.advanceDeduction),
        'Leave Deduction': leaveDeduction,
        'Extra Work Payment': extraWorkPayment,
        'Salary Payable': inHand + pfEsic,
        'Mobile & Petrol': toPayrollAmount(e?.reimbursements),
        'PF & ESIC DEDUCTION': pfEsic,
        'Net Amt. Payable': inHand,
        'Bank Transfer': toPayrollAmount(byBank),
        Other: toPayrollAmount(other),
        'Balance Advance': toPayrollAmount(e?.balanceAdvance),
      };
    });
  };

  const openExportPayRun = (data) => {
    const pr = data || payRunData;
    if (!pr?.payRunId) {
      toast.error('No pay run data to export');
      return;
    }
    setExportTarget(pr);
    setExportOpen(true);
  };

  const handleExportPayRun = async (format, selectedColumns) => {
    if (exporting || !exportTarget?.payRunId) return;
    setExporting(true);
    try {
      if (format === 'excel') {
        const response = await payrollApi.exportSheet(exportTarget.payRunId, payrollSortParams());
        const blob = await response.blob();
        const disposition = response.headers.get('content-disposition') || '';
        const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
        const fileName =
          matchedFileName ||
          `PayRun_${exportTarget.year}_${String(exportTarget.month).padStart(2, '0')}.xlsx`;
        downloadBlobFile(blob, fileName);
        setExportOpen(false);
        setExportTarget(null);
        toast.success('Pay run exported');
        return;
      }

      const detail =
        unwrapPayRunDetail(
          await payrollApi.getById(exportTarget.payRunId, payrollSortParams())
        ) || exportTarget;
      const allRows = buildPayRunExportRows(detail);
      if (!allRows.length) {
        toast.error('No pay run data to export');
        return;
      }

      const monthName = MONTHS[(exportTarget.month || 1) - 1] || MONTHS[0];
      let joined = [];
      let exited = [];
      try {
        const movementResponse = await employeeApi.getJoinedExited({
          month: monthName,
          year: exportTarget.year,
          ...(selectedProject !== 'all' ? { project: selectedProject } : {}),
        });
        ({ joined, exited } = normalizeJoinedExitedResponse(movementResponse));
      } catch (err) {
        console.error('Joined/exited export fetch failed:', err);
        toast.warning('Payroll exported, but joined/exited employees could not be loaded');
      }
      const joinedRows = buildJoinedExitedExportRows(
        filterJoinedForMonth(joined, exportTarget.month, exportTarget.year)
      );
      const exitedRows = buildJoinedExitedExportRows(
        filterExitedForMonth(exited, exportTarget.month, exportTarget.year)
      );

      const header =
        Array.isArray(selectedColumns) && selectedColumns.length > 0
          ? selectedColumns
          : PAYRUN_EXPORT_HEADER;

      const rows = allRows.map((row) =>
        header.reduce((acc, key) => {
          acc[key] = row?.[key] ?? '';
          return acc;
        }, {})
      );

      const pdfTotalRow = buildPayRunPdfTotalRow(rows, header);
      const fileBase = `PayRun_${exportTarget.year}_${String(exportTarget.month).padStart(2, '0')}`;
      const lastDay = new Date(exportTarget.year, exportTarget.month, 0).getDate();
      const monthLabel = monthName.toUpperCase();
      const reportTitle = `SALARY SHEET FROM 01-${monthLabel} ${exportTarget.year} TO ${lastDay} ${monthLabel} ${exportTarget.year}`;

      exportMultiSectionPdf(
        [
          {
            title: 'Salary Sheet',
            rows,
            header,
            footerRow: pdfTotalRow,
            columnWeights: PAYRUN_PDF_COLUMN_WEIGHTS,
            numericColumns: PAYRUN_PDF_NUMERIC_COLUMNS,
            compactColumns: PAYRUN_PDF_NARROW_COLUMNS,
            headerLabels: PAYRUN_PDF_HEADER_LABELS,
          },
          {
            layout: 'split',
            left: [
              joinedRows.length
                ? {
                    title: `Employees Joined (${monthName} ${exportTarget.year})`,
                    rows: joinedRows,
                    header: JOINED_EXITED_EXPORT_HEADER,
                  }
                : null,
              exitedRows.length
                ? {
                    title: `Employees Exited (${monthName} ${exportTarget.year})`,
                    rows: exitedRows,
                    header: JOINED_EXITED_EXPORT_HEADER,
                  }
                : null,
            ].filter(Boolean),
            right: {
              title: '',
              rows: buildMonthDaysSummaryRows(exportTarget.month, exportTarget.year),
              header: MONTH_DAYS_SUMMARY_HEADER,
              hideHead: true,
              tableWidth: 90,
              highlightRowLabels: ['Balance'],
            },
          },
        ],
        {
          title: reportTitle,
          fileName: fileBase,
          orientation: 'landscape',
        }
      );

      setExportOpen(false);
      setExportTarget(null);
      toast.success('Pay run exported');
    } catch (err) {
      toast.error(err?.message || 'Failed to export pay run');
    } finally {
      setExporting(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      'Draft': 'bg-gray-100 text-gray-700 border-gray-200',
      'Frozen': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Processed': 'bg-green-100 text-green-700 border-green-200',
    };
    return map[status] || map.Draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="payroll-page" className="space-y-3 animate-fadeIn">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Payroll Processing</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          {canEdit ? 'Create and manage monthly pay runs' : 'View monthly pay run details'}
        </p>
      </div>

      <Tabs defaultValue="process" className="space-y-4">
        <TabsList>
          <TabsTrigger value="process" data-testid="tab-process">
            <Receipt className="w-4 h-4 mr-1" /> Process Payroll
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-1" /> Pay Run History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="process" className="space-y-4 sm:space-y-6">
          <Card className="border border-border shadow-sm">
            <CardContent className="p-3 sm:p-4 flex flex-wrap items-end gap-3 sm:gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">Month</label>
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v, 10))}>
                  <SelectTrigger className="w-[140px] sm:w-[160px]" data-testid="payroll-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">Year</label>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v, 10))}>
                  <SelectTrigger className="w-[100px]" data-testid="payroll-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">Company</label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger className="w-[150px] sm:w-[180px]" data-testid="payroll-company-select">
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies.filter((company) => company?.name).map((company) => (
                      <SelectItem key={company?.companyId ?? company?.id ?? company?.name} value={company?.companyId ?? company?.id ?? company?.name}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">Project</label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger className="w-[150px] sm:w-[180px]" data-testid="payroll-project-select">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.filter((project) => project?.name).map((project) => (
                      <SelectItem key={project?.projectId ?? project?.id ?? project?.name} value={project?.projectId ?? project?.id ?? project?.name}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {canEdit && (
              <Button
                onClick={handleGeneratePayRunClick}
                disabled={generating}
                className="bg-[#0047AB] hover:bg-[#003d96] text-white"
                data-testid="generate-payrun-btn"
              >
                {generating ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Generate Pay Run
              </Button>
              )}
            </CardContent>
          </Card>

          {payRunData && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
                {[
                  { label: 'Employees', value: payRunData.employeeCount, color: '#0047AB' },
                  {
                    label: 'Calendar Days',
                    value: payRunData.daysInMonth ?? payRunData.employees?.[0]?.daysInMonth ?? '—',
                    color: '#0ea5e9',
                  },
                  { label: 'Total Gross', value: formatCurrency(payRunData.totalGross), color: '#10b981' },
                  { label: 'Total Deductions', value: formatCurrency(payRunData.totalDeductions), color: '#ef4444' },
                  { label: 'Total Net Pay', value: formatCurrency(payRunData.totalNet), color: '#6366f1' },
                ].map(s => (
                  <Card key={s.label} className="border border-border shadow-sm">
                    <CardContent className="p-3 sm:p-4 text-center">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</p>
                      <p className="text-base sm:text-lg font-bold font-mono mt-1" style={{ color: s.color }}>{s.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Status & Actions */}
              <Card className="border border-border shadow-sm">
                <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
                    <span className="text-sm font-medium">Pay Run Status:</span>
                    <Badge className={statusBadge(payRunData.status)}>{payRunData.status}</Badge>
                    {payRunData.status === 'Frozen' && <span className="text-xs text-muted-foreground">(Yet to Pay)</span>}
                    <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-2 min-w-0">
                      <div className="relative min-w-0 flex-1 sm:w-[220px] sm:flex-none">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          data-testid="payroll-search"
                          placeholder="Search by employee name..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
                      <Select
                        value={salarySortOrder || undefined}
                        onValueChange={setSalarySortOrder}
                      >
                        <SelectTrigger
                          className="h-9 w-[11.5rem] shrink-0 gap-2 [&>span]:flex [&>span]:items-center [&>span]:gap-2"
                          aria-label="Sort by salary"
                          data-testid="payroll-salary-sort-select"
                        >
                          <ArrowUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <SelectValue placeholder="Sort by salary" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="desc">Salary: High to Low</SelectItem>
                          <SelectItem value="asc">Salary: Low to High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canEdit && payRunData.status === 'Draft' && (
                      <>
                        <Button variant="outline" onClick={() => setConfirmAction('saveDraft')} disabled={generating} data-testid="recalculate-btn">Save Draft</Button>
                        <Button onClick={() => setConfirmAction('freeze')} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-testid="freeze-payrun-btn">
                          <Lock className="w-4 h-4 mr-2" /> Freeze Pay Run
                        </Button>
                      </>
                    )}
                    {canEdit && payRunData.status === 'Frozen' && (
                      <Button onClick={() => setConfirmAction('process')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="process-payrun-btn">
                        <CheckCircle className="w-4 h-4 mr-2" /> Process Payroll
                      </Button>
                    )}
                    {canEdit && (
                    <Button variant="outline" onClick={() => openExportPayRun()} data-testid="export-payrun-btn">
                      <Download className="w-4 h-4 mr-2" /> Export
                    </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Table */}
              <Card className="border border-border shadow-sm">
                <CardContent className="p-0">
                  <Table containerClassName="max-h-[70vh] overflow-auto">
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-[10px] uppercase tracking-widest font-medium sticky top-0 left-0 bg-muted z-30 min-w-[150px] shadow-[0_1px_0_0_hsl(var(--border))]">Employee</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-blue-50 shadow-[0_1px_0_0_hsl(var(--border))]">Work Days</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-green-50 shadow-[0_1px_0_0_hsl(var(--border))]">Present</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-red-50 shadow-[0_1px_0_0_hsl(var(--border))]">Absent</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-purple-50 shadow-[0_1px_0_0_hsl(var(--border))]">Leave</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-orange-50 shadow-[0_1px_0_0_hsl(var(--border))]">Manual LOP</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-emerald-50 shadow-[0_1px_0_0_hsl(var(--border))]">Paid Days</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Gross (A)</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Reimb (B)</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-right text-amber-700 sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Advance</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-right text-red-600 sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Deductions (C)</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-right font-bold text-[#10b981] sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">In Hand (A+B-C)</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Bonus Days</TableHead>
                        {showAnnualBonusColumn && (
                          <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">
                            Annual Bonus
                          </TableHead>
                        )}
                        {/* <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-muted">Bonus (D)</TableHead> */}
                        <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Employer (E)</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-sky-50 shadow-[0_1px_0_0_hsl(var(--border))]">Bank Transfer</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-violet-50 shadow-[0_1px_0_0_hsl(var(--border))]">Other</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Slip</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedProcessEmps.map(emp => (
                        <TableRow key={emp.employeeId} data-testid={`payrun-row-${emp.employeeId}`}>
                          <TableCell className="sticky left-0 bg-background z-10 border-r">
                            <div>
                              {emp?.employeeId ? (
                                <Link
                                  to={`/employees/${emp.employeeId}`}
                                  className="text-sm font-medium hover:text-[#0047AB] hover:underline"
                                  data-testid={`payroll-employee-link-${emp.employeeId}`}
                                >
                                  {emp.employeeName}
                                </Link>
                              ) : (
                                <p className="text-sm font-medium">{emp.employeeName}</p>
                              )}
                              <p className="text-[10px] text-muted-foreground font-mono">{emp.employeeId} &middot; {emp.department}</p>
                            </div>
                          </TableCell>
                          {/* Attendance Stats */}
                          <TableCell className="text-center bg-blue-50/30">
                            <div className="flex flex-col items-center">
                              <span className="font-mono text-xs">{emp.workingDays}</span>
                              <span className="mt-0.5 text-[9px] text-blue-600">
                                {emp?.monthlyLeaveAllowanceAll
                                  ? 'All'
                                  : `off: ${emp?.weeklyOffDaysTaken ?? 0}`}
                              </span>
                              <span className="text-[9px] text-blue-600">
                                {emp?.weeklyOffDays?.length ? emp.weeklyOffDays.join(', ') : '—'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-mono text-xs bg-green-50/30 text-green-700 font-medium">{emp.presentDays}</TableCell>
                          <TableCell className="text-center font-mono text-xs bg-red-50/30 text-red-600">{emp.absentDays || 0}</TableCell>
                          <TableCell className="text-center font-mono text-xs bg-purple-50/30 text-purple-600">{emp.leaveDays || 0}</TableCell>
                          <TableCell className="text-center bg-orange-50/30" data-testid={`lop-cell-${emp.employeeId}`}>
                            {payRunData.status === 'Draft' && canEdit ? (
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  step="0.5"
                                  min={0}
                                  className="w-16 text-center font-mono text-xs h-7"
                                  value={editableData[emp.employeeId]?.manualLopAdd ?? emp.manualLopAdd ?? 0}
                                  onChange={e => handleManualLopAddChange(emp.employeeId, e.target.value)}
                                  onBlur={(e) => handleManualLopAddBlur(emp.employeeId, e.target.value)}
                                  title={`Auto LOP from attendance: ${emp.lopFromAttendance || 0}. Manual LOP cannot exceed calendar days (${emp.daysInMonth ?? payRunData?.daysInMonth ?? 0}).`}
                                  data-testid={`lop-input-${emp.employeeId}`}
                                />
                                <span className="text-[9px] text-orange-500 mt-0.5">
                                  auto: {emp.lopFromAttendance || 0} | total: {emp.lopDays || 0}
                                </span>
                              </div>
                            ) : (
                              <span className="font-mono text-xs text-orange-600 font-medium">{emp.lopDays}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center font-mono text-xs bg-emerald-50/30 text-emerald-700 font-bold">{emp.paidDays}</TableCell>
                          {/* CTC Summary */}
                          <TableCell className="text-right font-mono text-xs">{formatCurrency(emp.effectiveGross)}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{formatCurrency(emp.reimbursements)}</TableCell>
                          <TableCell className="text-right font-mono text-xs text-amber-700">
                            {payRunData.status === 'Draft' && canEdit ? (
                              <Input
                                type="number"
                                step="1"
                                min={0}
                                className="w-20 ml-auto text-right font-mono text-xs h-7"
                                value={editableData[emp.employeeId]?.advanceOverride ?? emp?.advanceDeduction ?? 0}
                                onChange={(e) => handleAdvanceOverrideChange(emp.employeeId, e.target.value)}
                                title="Advance deduction override for this employee"
                                data-testid={`advance-input-${emp.employeeId}`}
                              />
                            ) : (
                              formatCurrency(emp?.advanceDeduction ?? 0)
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-red-600">{formatCurrency(emp.totalDeductions)}</TableCell>
                          <TableCell className="text-right font-mono text-xs font-bold text-[#10b981]">
                            <InHandAmount employee={emp} />
                          </TableCell>
                          <TableCell className="text-center font-mono text-xs">
                            {payRunData.status === 'Draft' && canEdit ? (
                              <Input
                                type="number"
                                step="0.5"
                                min={0}
                                className="w-16 text-center font-mono text-xs h-7"
                                value={editableData[emp.employeeId]?.bonusDays ?? emp.bonusDays ?? 0}
                                onChange={(e) => handleBonusDaysChange(emp.employeeId, e.target.value)}
                                title="Bonus days for this employee"
                                data-testid={`bonus-days-input-${emp.employeeId}`}
                              />
                            ) : (
                              (emp.bonusDays ?? 0)
                            )}
                          </TableCell>
                          {showAnnualBonusColumn && (
                            <TableCell className="text-right font-mono text-xs">
                              {formatCurrency(emp?.netAnnualBonus ?? 0)}
                            </TableCell>
                          )}
                          {/* <TableCell className="text-right font-mono text-xs">{formatCurrency(emp.bonus ?? 0)}</TableCell> */}
                          <TableCell className="text-right font-mono text-xs">{formatCurrency(emp.employerContribTotal || 0)}</TableCell>
                          <TableCell className="text-right font-mono text-xs bg-sky-50/30">
                            {payRunData.status === 'Draft' && canEdit ? (
                              <Input
                                type="number"
                                step="1"
                                min={0}
                                max={emp.inHandSalary ?? emp.netPay ?? 0}
                                className="w-24 ml-auto text-right font-mono text-xs h-7"
                                value={
                                  editableData[emp.employeeId]?.byBank ??
                                  emp.byBank ??
                                  resolvePaymentSplit(emp.inHandSalary ?? emp.netPay, {}, emp).byBank
                                }
                                onChange={(e) => handleByBankChange(emp.employeeId, e.target.value)}
                                title="Bank transfer amount (Bank Transfer + Other = In Hand)"
                                data-testid={`by-bank-input-${emp.employeeId}`}
                              />
                            ) : (
                              formatCurrency(
                                emp.byBank ??
                                  resolvePaymentSplit(emp.inHandSalary ?? emp.netPay, {}, emp).byBank
                              )
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs bg-violet-50/30">
                            {payRunData.status === 'Draft' && canEdit ? (
                              <Input
                                type="number"
                                step="1"
                                min={0}
                                max={emp.inHandSalary ?? emp.netPay ?? 0}
                                className="w-24 ml-auto text-right font-mono text-xs h-7"
                                value={
                                  editableData[emp.employeeId]?.other ??
                                  emp.other ??
                                  resolvePaymentSplit(emp.inHandSalary ?? emp.netPay, {}, emp).other
                                }
                                onChange={(e) => handleOtherChange(emp.employeeId, e.target.value)}
                                title="Other payment amount (reduces Bank Transfer; total must equal In Hand)"
                                data-testid={`other-input-${emp.employeeId}`}
                              />
                            ) : (
                              formatCurrency(
                                emp.other ??
                                  resolvePaymentSplit(emp.inHandSalary ?? emp.netPay, {}, emp).other
                              )
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7"
                              onClick={() => {
                                const fullEmp = employees.find(e => e.empId === emp.employeeId || e.id === emp.employeeId);
                                setPaySlipEmp(fullEmp);
                                setPaySlipData(emp);
                                setPaySlipOpen(true);
                              }}
                              data-testid={`payslip-btn-${emp.employeeId}`}
                            >
                              <Printer className="w-3 h-3 mr-1" /> Slip
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {processEmployees.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={processTableColSpan} className="text-center py-8 text-muted-foreground">
                            {debouncedSearch?.trim() ? 'No employees match your search' : 'No employees in this pay run'}
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Totals Row */}
                      {processEmployees.length > 0 && (
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell className="sticky left-0 bg-muted/50 z-10 text-sm font-bold">TOTAL</TableCell>
                        <TableCell colSpan={6}></TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(payRunData.totalGross)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right font-mono text-sm text-amber-700">
                          {formatCurrency(payRunData.employees?.reduce((sum, emp) => sum + (emp?.advanceDeduction ?? 0), 0))}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-red-600">{formatCurrency(payRunData.totalDeductions)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-[#0047AB]">{formatCurrency(payRunData.totalNet)}</TableCell>
                        <TableCell></TableCell>
                        {showAnnualBonusColumn && (
                          <TableCell className="text-right font-mono text-sm">
                            {formatCurrency(
                              processEmployees.reduce(
                                (sum, emp) => sum + toPayrollAmount(emp?.netAnnualBonus),
                                0
                              )
                            )}
                          </TableCell>
                        )}
                        <TableCell colSpan={4}></TableCell>
                      </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {processEmployees.length > 0 && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-sm text-muted-foreground">
                      Page {processPage} of {processTotalPages} ({processEmployees.length} employees)
                    </p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">Show</span>
                      <Select
                        value={String(processPageSize)}
                        onValueChange={(v) => {
                          setProcessPageSize(Number(v));
                          setProcessPage(1);
                        }}
                      >
                        <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="process-page-size-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-muted-foreground">per page</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setProcessPage((prev) => Math.max(prev - 1, 1))}
                      disabled={processPage <= 1}
                      data-testid="process-prev-page-btn"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setProcessPage((prev) => Math.min(prev + 1, processTotalPages))}
                      disabled={processPage >= processTotalPages}
                      data-testid="process-next-page-btn"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {!payRunData && (
            <Card className="border border-border shadow-sm">
              <CardContent className="p-12 text-center">
                <Receipt className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-muted-foreground">
                  {canEdit
                    ? 'Select a month and generate a pay run to begin payroll processing'
                    : 'Select a month to view pay run details'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card className="border border-border shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="payroll-history-search"
                  placeholder="Search by employee name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-0">
              <Table containerClassName="overflow-visible">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-widest sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Pay Run ID</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Month</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Employees</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Total Gross</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Total Net</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Processed On</TableHead>
                    {canEdit && <TableHead className="text-xs uppercase tracking-widest sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedHistory.map(pr => (
                    <TableRow key={pr.payRunId} data-testid={`payrun-history-${pr.payRunId}`}>
                      <TableCell className="font-mono text-xs">{pr.payRunId}</TableCell>
                      <TableCell className="text-sm">{MONTHS[pr.month - 1]} {pr.year}</TableCell>
                      <TableCell className="font-mono text-sm">{pr.employeeCount}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(pr.totalGross)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold text-[#0047AB]">{formatCurrency(pr.totalNet)}</TableCell>
                      <TableCell><Badge className={statusBadge(pr.status)}>{pr.status}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{pr.processedAt ? new Date(pr.processedAt).toLocaleDateString() : '-'}</TableCell>
                      {canEdit && (
                      <TableCell>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => openExportPayRun(pr)} data-testid={`export-history-${pr.payRunId}`}>
                          <Download className="w-3 h-3 mr-1" /> Export
                        </Button>
                      </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {allPayRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        {debouncedSearch?.trim() ? 'No pay runs match your search' : 'No pay run history'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {historyTotal > 0 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-muted-foreground">
                  Page {historyPage} of {historyTotalPages} ({historyTotal} pay runs)
                </p>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">Show</span>
                  <Select
                    value={String(historyPageSize)}
                    onValueChange={(v) => {
                      setHistoryPageSize(Number(v));
                      setHistoryPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="history-page-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">per page</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setHistoryPage((prev) => Math.max(prev - 1, 1))}
                  disabled={historyPage <= 1}
                  data-testid="history-prev-page-btn"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setHistoryPage((prev) => Math.min(prev + 1, historyTotalPages))}
                  disabled={historyPage >= historyTotalPages}
                  data-testid="history-next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Pay Slip Modal */}
      <PaySlipModal
        open={paySlipOpen}
        onOpenChange={setPaySlipOpen}
        employee={paySlipEmp}
        payData={paySlipData}
        month={selectedMonth}
        year={selectedYear}
      />

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent data-testid="payroll-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialogCopy[confirmAction]?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialogCopy[confirmAction]?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="payroll-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={confirmDialogCopy[confirmAction]?.actionClassName}
              data-testid="payroll-confirm-action"
            >
              {confirmDialogCopy[confirmAction]?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ExportFormatDialog
        open={exportOpen}
        onOpenChange={(open) => {
          setExportOpen(open);
          if (!open) setExportTarget(null);
        }}
        loading={exporting}
        title="Export Pay Run"
        description="Choose Excel or PDF. For PDF, pick which salary sheet columns to include."
        pdfColumns={PAYRUN_EXPORT_HEADER}
        defaultUnselectedPdfColumns={PAYRUN_PDF_UNSELECTED_COLUMNS}
        onExport={handleExportPayRun}
        data-testid="payroll-export-format-dialog"
      />
    </div>
  );
}
